// TextEditor/suggestion.tsx
import React, {
    useEffect,
    useState,
    useImperativeHandle,
    forwardRef,
  } from 'react';
  import { SuggestionProps } from '@tiptap/suggestion';
  import { ReactRenderer } from '@tiptap/react';
  import tippy, { Instance as TippyInstance } from 'tippy.js';
  import { User } from 'util/constants'
  import api from 'config/axiosConfig';
  import { PluginKey } from '@tiptap/pm/state';
  import { ExternalLink } from 'lucide-react';
  import ExternalMentionModal from 'components/Common/ExternalMentionModal';
  import { createPortal } from 'react-dom';
  import { apiRoutes } from 'util/apiRoutes';
  
  interface MentionItem {
    id: number;
    label: string;
    isExternal?: boolean;
  }

  interface ExternalMention {
    label: string;
    url: string;
    platform: string;
  }
  
  interface SuggestionItemProps {
    items: MentionItem[];
    command: (item: MentionItem) => void;
    editor: any;
    range: any;
    closeSuggestion?: () => void;
  }
  
  interface SuggestionListHandle {
    onKeyDown: ({ event }: { event: KeyboardEvent }) => boolean;
  }
  
  const SuggestionList = forwardRef<SuggestionListHandle, SuggestionItemProps>(
    ({ items, command, editor, range, closeSuggestion }, ref) => {
      const [selectedIndex, setSelectedIndex] = useState(0);
      const [showExternalModal, setShowExternalModal] = useState(false);

      // Always add external mention option
      const itemsWithExternal = [
        ...items,
        {
          id: -1,
          label: 'Mention someone outside Societea',
          isExternal: true
        }
      ];
  
      useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }: { event: KeyboardEvent }) => {
          if (event.key === 'ArrowUp') {
            setSelectedIndex((prevIndex) =>
              (prevIndex + itemsWithExternal.length - 1) % itemsWithExternal.length
            );
            event.preventDefault();
            return true;
          } else if (event.key === 'ArrowDown') {
            setSelectedIndex((prevIndex) => (prevIndex + 1) % itemsWithExternal.length);
            event.preventDefault();
            return true;
          } else if (event.key === 'Enter') {
            selectItem(selectedIndex);
            event.preventDefault();
            return true;
          }
          return false;
        },
      }));
  
      useEffect(() => {
        setSelectedIndex(0);
      }, [items]);
  
      const selectItem = (index: number) => {
        const item = itemsWithExternal[index];
        if (item) {
          if (item.isExternal) {
            // Hide suggestions popup before opening modal to avoid overlay and click-outside conflicts
            if (typeof closeSuggestion === 'function') {
              try { closeSuggestion(); } catch {}
            }
            setShowExternalModal(true);
          } else {
            command(item);
          }
        }
      };

      const handleExternalMentionAdd = (externalMention: ExternalMention) => {
        // Insert the external mention into the editor
        editor
          .chain()
          .focus()
          .insertContentAt(range, [
            {
              type: 'mention',
              attrs: {
                label: externalMention.label,
                url: externalMention.url,
                platform: externalMention.platform,
                isExternal: true,
              },
            },
            { type: 'text', text: ' ' },
          ])
          .run();
        setShowExternalModal(false);
      };
  
      return (
        <>
          <div
            className="bg-transparent rounded-md shadow-lg max-h-60 overflow-auto z-50"
            onMouseDown={(e) => {
              // Prevent tippy from closing before our click handler fires
              e.preventDefault();
            }}
          >
            {itemsWithExternal.map((item, index) => (
              <div
                key={item.id}
                className={`px-2 py-2 my-1 rounded-lg cursor-pointer ${
                  index === selectedIndex ? 'bg-gray-600 dark:bg-gray-700' : 'hover:bg-gray-600 dark:hover:bg-gray-700'
                } ${item.isExternal ? 'border-t border-gray-500' : ''}`}
                onClick={() => selectItem(index)}
              >
                {item.isExternal ? (
                  <div className="flex items-center space-x-2 text-gray-400">
                    <ExternalLink className="size-4" />
                    <span className="italic">{item.label}</span>
                  </div>
                ) : (
                  item.label
                )}
              </div>
            ))}
          </div>
          
          {showExternalModal && createPortal(
            <ExternalMentionModal
              onClose={() => setShowExternalModal(false)}
              onAdd={handleExternalMentionAdd}
            />,
            document.body
          )}
        </>
      );
    }
  );
  
  const userSuggestion = {
    char: '@',
    pluginKey: new PluginKey('userSuggestion'),
    startOfLine: false,
    items: async ({ query }: { query: string }) => {
      if (!query) return [];
      try {
        const response = await api.get<User[]>(apiRoutes.user.search, { params: { q: query } });
        // Map users to MentionItem[]
        return response.data.map((user) => ({
          id: user.id,
          label: user.username,
        }));
      } catch (error) {
        console.error('Error fetching users:', error);
        return [];
      }
    },
    command: ({ editor, range, props }: any) => {
      const item = props as MentionItem;
      
      // Only handle regular user mentions here
      // External mentions are handled in the modal callback
      if (!item.isExternal) {
        editor
          .chain()
          .focus()
          .insertContentAt(range, [
            {
              type: 'mention',
              attrs: {
                id: item.id,
                label: item.label,
                isExternal: false,
              },
            },
            { type: 'text', text: ' ' },
          ])
          .run();
      }
    },
    render: () => {
      let component: ReactRenderer<SuggestionListHandle>;
      let popup: TippyInstance[];
  
      return {
        onStart: (props: SuggestionProps) => {
          component = new ReactRenderer(SuggestionList, {
            props: {
              ...props,
              editor: props.editor,
              range: props.range,
              closeSuggestion: () => {
                try {
                  if (popup && popup[0]) { popup[0].hide(); }
                } catch {}
              }
            } as any,
            editor: props.editor,
          });
  
          popup = tippy('body', {
            getReferenceClientRect: props.clientRect as any,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            // Keep popper open during mousedown so our click handler can run and open the modal
            trigger: 'manual',
            hideOnClick: false,
            placement: 'bottom-start',
          });
          // Manually show the popup since we set trigger to manual
          if (popup && popup[0]) { popup[0].show(); }
        },
        onUpdate(props: SuggestionProps) {
          component.updateProps({
            ...props,
            editor: props.editor,
            range: props.range,
          });
  
          popup[0].setProps({
            getReferenceClientRect: props.clientRect as any,
          });
        },
        onKeyDown({ event }: { event: KeyboardEvent }) {
          if (event.key === 'Escape') {
            popup[0].hide();
            return true;
          }
  
          // Allow the SuggestionList to handle the event
          return component.ref?.onKeyDown({ event }) ?? false;
        },
        onExit() {
          popup[0].destroy();
          component.destroy();
        },
      };
    },
  };
  
  export default userSuggestion;
  